import * as React from 'react';
import {ExerciseLibraryModalProps, Exercise} from './ExerciseLibraryModal.type';
import {useExerciseLibraryModal} from './ExerciseLibraryModal.use-case';
import {ExerciseLibraryModalMap} from './ExerciseLibraryModal.map';
import {CreateExercise} from '../CreateExercise';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ExerciseList,
  SearchContainer,
  SearchInput,
  ExerciseGrid,
  ExerciseCard,
  ExerciseImage,
  ExerciseName,
  CheckMark,
  PreviewPanel,
  PreviewContainer,
  PreviewImage,
  PreviewTitle,
  PreviewDescription,
  FormSection,
  FormRow,
  FormField,
  FormLabel,
  FormInput,
  FormTextarea,
  ModalFooter,
  Button,
  LoadingContainer,
  LoadMoreButton,
  EmptyState,
  NoExerciseSelected,
  TabsContainer,
  TabButton,
} from './ExerciseLibraryModal.style';

export const ExerciseLibraryModal = ({
  isOpen,
  onClose,
  onSelectExercise,
  workoutGroupIndex,
  workoutIndex,
  initialExerciseData,
  isEditing = false,
}: ExerciseLibraryModalProps): JSX.Element | null => {
  const _useUseCase = useExerciseLibraryModal(initialExerciseData);

  const handleCloseModal = React.useCallback(() => {
    _useUseCase.resetState();
    onClose();
  }, [_useUseCase, onClose]);

  const handleExerciseClick = React.useCallback(
    (exercise: Exercise) => {
      _useUseCase.setSelectedExercise(exercise);
    },
    [_useUseCase],
  );

  const handleInputChange = React.useCallback(
    (field: string, value: string) => {
      _useUseCase.updateExerciseData(field as any, value);
    },
    [_useUseCase],
  );

  const handleConfirmSelection = React.useCallback(() => {
    if (_useUseCase.canSelectExercise()) {
      const exerciseData = _useUseCase.getSelectedExerciseData();
      onSelectExercise(exerciseData);
      handleCloseModal();
    }
  }, [_useUseCase, onSelectExercise, handleCloseModal]);

  const renderExerciseCard = React.useCallback(
    (exercise: Exercise) => {
      const isSelected = _useUseCase.selectedExercise?.id === exercise.id;
      const imageUrl = ExerciseLibraryModalMap.getImageUrl(exercise);

      return (
        <ExerciseCard
          key={exercise.id}
          isSelected={isSelected}
          onClick={() => handleExerciseClick(exercise)}>
          {isSelected && <CheckMark>✓</CheckMark>}
          <ExerciseImage>
            {imageUrl ? (
              <img src={imageUrl} alt={exercise.name} />
            ) : (
              <span style={{color: '#999'}}>Sem imagem</span>
            )}
          </ExerciseImage>
          <ExerciseName>{exercise.name}</ExerciseName>
        </ExerciseCard>
      );
    },
    [_useUseCase.selectedExercise, handleExerciseClick],
  );

  const renderPreviewPanel = React.useCallback(() => {
    if (!_useUseCase.selectedExercise) {
      return (
        <NoExerciseSelected>
          Selecione um exercício para ver os detalhes
        </NoExerciseSelected>
      );
    }

    const previewData = ExerciseLibraryModalMap.formatExercisePreviewData(
      _useUseCase.exerciseData,
    );
    if (!previewData) return null;

    return (
      <>
        <PreviewContainer>
          <PreviewImage>
            {previewData.imageUrl ? (
              <img src={previewData.imageUrl} alt={previewData.name} />
            ) : (
              <span style={{color: '#999'}}>Sem imagem</span>
            )}
          </PreviewImage>
          <PreviewTitle>{previewData.name}</PreviewTitle>
          <PreviewDescription>{previewData.description}</PreviewDescription>
        </PreviewContainer>

        <FormSection>
          <FormRow>
            <FormField>
              <FormLabel>Séries</FormLabel>
              <FormInput
                type="text"
                value={_useUseCase.exerciseData.series}
                onChange={(e) => handleInputChange('series', e.target.value)}
                placeholder="Ex: 3"
              />
            </FormField>
            <FormField>
              <FormLabel>Repetições</FormLabel>
              <FormInput
                type="text"
                value={_useUseCase.exerciseData.reps}
                onChange={(e) => handleInputChange('reps', e.target.value)}
                placeholder="Ex: 12"
              />
            </FormField>
          </FormRow>

          <FormRow>
            <FormField>
              <FormLabel>Carga</FormLabel>
              <FormInput
                type="text"
                value={_useUseCase.exerciseData.weight}
                onChange={(e) => handleInputChange('weight', e.target.value)}
                placeholder="Ex: 10kg"
              />
            </FormField>
            <FormField>
              <FormLabel>Tempo</FormLabel>
              <FormInput
                type="text"
                value={_useUseCase.exerciseData.time}
                onChange={(e) => handleInputChange('time', e.target.value)}
                placeholder="Ex: 30s"
              />
            </FormField>
          </FormRow>

          <FormRow>
            <FormField>
              <FormLabel>Tempo de Descanso</FormLabel>
              <FormInput
                type="text"
                value={_useUseCase.exerciseData.restTime}
                onChange={(e) => handleInputChange('restTime', e.target.value)}
                placeholder="Ex: 60s"
              />
            </FormField>
          </FormRow>

          <FormField>
            <FormLabel>Observações</FormLabel>
            <FormTextarea
              value={_useUseCase.exerciseData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Observações sobre a execução do exercício..."
            />
          </FormField>
        </FormSection>
      </>
    );
  }, [
    _useUseCase.selectedExercise,
    _useUseCase.exerciseData,
    handleInputChange,
  ]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleCloseModal}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {isEditing ? 'Editar Exercício' : 'Exercícios'}
          </ModalTitle>
          <CloseButton onClick={handleCloseModal}>×</CloseButton>
        </ModalHeader>

        <TabsContainer>
          <TabButton
            type="button"
            isActive={_useUseCase.activeTab === 'library'}
            onClick={() => _useUseCase.setActiveTab('library')}>
            <i className="far fa-search" style={{marginRight: '8px'}} />
            Biblioteca
          </TabButton>
          <TabButton
            type="button"
            isActive={_useUseCase.activeTab === 'create'}
            onClick={() => _useUseCase.setActiveTab('create')}>
            <i className="far fa-plus" style={{marginRight: '8px'}} />
            Criar Novo
          </TabButton>
        </TabsContainer>

        {_useUseCase.activeTab === 'library' ? (
          <>
            <ModalBody>
              <ExerciseList>
                <SearchContainer>
                  <SearchInput
                    type="text"
                    placeholder="Pesquisar exercícios..."
                    value={_useUseCase.searchTerm}
                    onChange={(e) =>
                      _useUseCase.handleSearchChange(e.target.value)
                    }
                  />
                </SearchContainer>

                {_useUseCase.isLoading && _useUseCase.exercises.length === 0 ? (
                  <LoadingContainer>
                    <span>Carregando exercícios...</span>
                  </LoadingContainer>
                ) : _useUseCase.exercises.length > 0 ? (
                  <>
                    <ExerciseGrid>
                      {_useUseCase.exercises.map(renderExerciseCard)}
                    </ExerciseGrid>

                    {_useUseCase.hasMore && (
                      <LoadMoreButton
                        onClick={_useUseCase.loadMore}
                        disabled={_useUseCase.isLoading}>
                        {_useUseCase.isLoading
                          ? 'Carregando...'
                          : 'Carregar mais exercícios'}
                      </LoadMoreButton>
                    )}
                  </>
                ) : (
                  <EmptyState>
                    <span>Nenhum exercício encontrado</span>
                  </EmptyState>
                )}
              </ExerciseList>

              <PreviewPanel>{renderPreviewPanel()}</PreviewPanel>
            </ModalBody>

            <ModalFooter>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmSelection}
                disabled={!_useUseCase.canSelectExercise()}>
                {isEditing ? 'Salvar Alterações' : 'Adicionar Exercício'}
              </Button>
            </ModalFooter>
          </>
        ) : (
          <CreateExercise
            formData={_useUseCase.formData}
            isCreating={_useUseCase.isCreatingExercise}
            isVideoUploading={_useUseCase.isVideoUploading}
            videoUploadUrl={_useUseCase.videoUploadUrl}
            onFormDataChange={_useUseCase.updateFormData}
            onVideoUpload={_useUseCase.setVideoUploadUrl}
            onVideoUploadStatus={_useUseCase.setVideoUploading}
            onCreate={_useUseCase.createExercise}
            onCancel={() => _useUseCase.setActiveTab('library')}
          />
        )}
      </ModalContainer>
    </ModalOverlay>
  );
};
